/**
 * Framework-independent timezone discovery and presentation helpers.
 *
 * Timezones here are display/business context only. These helpers do not
 * choose an account timezone, persist a preference, or read/write URL state.
 */

export const UTC_TIME_ZONE = "UTC";

// Older browsers may not expose Intl.supportedValuesOf(). Keep a practical
// compatibility catalogue without treating any regional zone as a default.
export const FALLBACK_TIME_ZONES = Object.freeze([
  UTC_TIME_ZONE,
  "Africa/Johannesburg",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "America/Toronto",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/Dublin",
  "Europe/London",
  "Europe/Paris",
  "Pacific/Auckland",
]);

const clean = (value) => (typeof value === "string" ? value.trim() : "");

const usableDate = (value) => {
  if (
    value === null ||
    value === undefined ||
    typeof value === "boolean" ||
    (typeof value === "string" && !value.trim()) ||
    (typeof value === "number" && !Number.isFinite(value))
  ) {
    return null;
  }
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Return true only when the runtime can resolve the supplied IANA zone. */
export function isValidIanaTimeZone(value, { intl = Intl } = {}) {
  const timeZone = clean(value);
  if (!timeZone || timeZone !== value || typeof intl?.DateTimeFormat !== "function") {
    return false;
  }

  try {
    const formatter = intl.DateTimeFormat("en-US", { timeZone });
    return Boolean(formatter.resolvedOptions().timeZone);
  } catch {
    return false;
  }
}

/**
 * Detect the browser zone without making it a business default.
 * UTC is a technical fail-safe when the runtime cannot provide a valid zone.
 */
export function detectBrowserTimeZone({
  fallback = UTC_TIME_ZONE,
  intl = Intl,
} = {}) {
  try {
    const detected = clean(intl?.DateTimeFormat?.().resolvedOptions().timeZone);
    if (isValidIanaTimeZone(detected, { intl })) return detected;
  } catch {
    // Fall through to the explicitly supplied technical fallback.
  }

  return isValidIanaTimeZone(fallback, { intl }) ? fallback : UTC_TIME_ZONE;
}

/** Build a sorted, de-duplicated catalogue supported by the current runtime. */
export function supportedTimeZones({ intl = Intl } = {}) {
  let runtimeValues = [];
  try {
    if (typeof intl?.supportedValuesOf === "function") {
      const discovered = intl.supportedValuesOf("timeZone");
      runtimeValues = Array.isArray(discovered) ? discovered : [];
    }
  } catch {
    // The compatibility list below remains available.
  }

  return Array.from(new Set([...FALLBACK_TIME_ZONES, ...runtimeValues]))
    .filter((value) => isValidIanaTimeZone(value, { intl }))
    .sort((left, right) => left.localeCompare(right));
}

const offsetMinutesAt = (timeZone, at, intl) => {
  const date = usableDate(at);
  if (!date || !isValidIanaTimeZone(timeZone, { intl })) return null;

  try {
    const parts = intl
      .DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .reduce((result, part) => ({ ...result, [part.type]: Number(part.value) }), {});

    const representedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    const minutes = Math.round((representedAsUtc - date.getTime()) / 60_000);
    return Number.isFinite(minutes) ? minutes : null;
  } catch {
    return null;
  }
};

/** Return an unambiguous offset such as UTC+05:30 for the supplied instant. */
export function timeZoneOffsetLabel(
  timeZone,
  { at = new Date(), intl = Intl } = {}
) {
  const minutes = offsetMinutesAt(timeZone, at, intl);
  if (minutes === null) return "";

  const sign = minutes < 0 ? "-" : "+";
  const absolute = Math.abs(minutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
  const remainder = String(absolute % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${remainder}`;
}

/** Human-readable IANA label with the offset applicable at `at`. */
export function timeZoneDisplayLabel(timeZone, options = {}) {
  const value = clean(timeZone);
  if (!value) return "";
  const offset = timeZoneOffsetLabel(value, options);
  const name = value.replaceAll("_", " ");
  return `${name}${offset ? ` (${offset})` : ""}`;
}

/**
 * Build select options while retaining an unknown stored value for safe
 * migration/review. Retention does not assert that the unknown value is valid.
 */
export function timezoneOptions(currentValue = "", options = {}) {
  const current = clean(currentValue);
  const values = new Set(supportedTimeZones(options));
  if (current) values.add(current);

  return Array.from(values)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({
      value,
      label: timeZoneDisplayLabel(value, options) || value,
    }));
}
